/**
 * @fileoverview Shared rich tooltip for profit-ranked dashboard bar charts
 *   (profit by country, top SKUs by profit). Renders the headline profit value
 *   plus a grid of secondary metrics (revenue, ad spend, ROAS, COGS).
 * @module shared/components/ui/ChartProfitTooltip
 */

import { Fragment, ReactElement, ReactNode } from 'react'
import { BadgeDollarSign } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

/**
 * A single secondary metric row shown beneath the headline profit.
 */
export interface ChartTooltipMetric {
  /** Localised label, e.g. "Revenue" */
  label: string
  /** Pre-formatted display value, e.g. "12.4K zł" or "3.9×" */
  value: string
}

interface ChartProfitTooltipProps {
  /** Leading visual — country flag span or product thumbnail */
  lead: ReactNode
  /** Entity name (country / product) */
  name: string
  /** Headline profit amount (signed) */
  profit: number
  /** Localised "Profit" label */
  profitLabel: string
  /** Compact money formatter (e.g. from useCompactMoney) */
  formatMoney: (amount: number) => string
  /** Secondary metric rows */
  metrics: ChartTooltipMetric[]
  /** Optional units-sold count, shown as a muted "13 × " prefix before the name */
  units?: string
}

/**
 * Glass-surface tooltip pairing a headline profit figure with secondary metrics.
 *
 * @example
 * ```tsx
 * <ChartProfitTooltip
 *   lead={<span>🇵🇱</span>}
 *   name="Poland"
 *   profit={2600}
 *   profitLabel="Profit"
 *   formatMoney={compactMoney}
 *   metrics={[{ label: 'Revenue', value: '12.4K zł' }]}
 * />
 * ```
 */
export function ChartProfitTooltip({
  lead,
  name,
  profit,
  profitLabel,
  formatMoney,
  metrics,
  units,
}: ChartProfitTooltipProps): ReactElement {
  const formatted = formatMoney(Math.abs(profit))
  const valueMatch = formatted.match(/^([+\-]?[\d.,]+[KMB]?)\s*(.*)$/)
  const numericPart = valueMatch?.[1] ?? formatted
  const currencyPart = valueMatch?.[2] ?? ''
  const prefix = profit >= 0 ? '+' : '-'
  const colorClass = profit >= 0 ? 'text-positive' : 'text-negative'

  return (
    <div className="flex max-w-[16rem] flex-col overflow-hidden rounded-lg border border-border/50 !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] shadow-xl">
      <div className="flex items-center gap-2 border-b border-border/40 bg-black/30 px-3 py-2">
        <span className="shrink-0">{lead}</span>
        <span className="text-sm leading-snug break-words">
          {units && <span className="text-muted-foreground">{units} × </span>}
          <span className="text-foreground">{name}</span>
        </span>
      </div>

      {metrics.length > 0 && (
        <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 px-3 py-2">
          {metrics.map(metric => (
            <Fragment key={metric.label}>
              <span className="text-sm text-muted-foreground">{metric.label}</span>
              <span className="text-sm font-mono font-medium text-foreground text-right whitespace-nowrap">
                {metric.value}
              </span>
            </Fragment>
          ))}
        </div>
      )}

      <div className="border-t border-border/40 bg-black/30 px-3 py-2">
        <p className="whitespace-nowrap">
          <span className={cn('text-2xl font-semibold tracking-tight font-mono', colorClass)}>
            {prefix}
            {numericPart}
          </span>
          {currencyPart && (
            <span className={cn('text-xl font-medium ml-1', colorClass)}>{currencyPart}</span>
          )}
        </p>
        <p className="text-sm text-muted-foreground inline-flex items-center">
          <BadgeDollarSign className="h-4 w-4 mr-1" />
          {profitLabel}
        </p>
      </div>
    </div>
  )
}
